// components/Availability/AvailabilityDashboard.js

import React from "react";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import {
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  StatusOnlineIcon,
  CogIcon,
  BellIcon,
  StarIcon,
  TrendingUpIcon,
  UserGroupIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";

const AvailabilityDashboard = ({
  admin,
  availability,
  status,
  stats,
  onNavigate,
}) => {
  // Get status color
  const getStatusColor = (statusValue) => {
    const colors = {
      AVAILABLE: "success",
      BUSY: "warning",
      IN_APPOINTMENT: "info",
      ON_BREAK: "warning",
      OFF_DUTY: "default",
      AWAY: "danger",
    };
    return colors[statusValue] || "default";
  };

  // Get status icon
  const getStatusIcon = (statusValue) => {
    const icons = {
      AVAILABLE: "✅",
      BUSY: "⚡",
      IN_APPOINTMENT: "👥",
      ON_BREAK: "☕",
      OFF_DUTY: "🏠",
      AWAY: "🚫",
    };
    return icons[statusValue] || "📍";
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get upcoming time off
  const getUpcomingTimeOff = () => {
    if (!availability?.timeOff) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return availability.timeOff
      .filter((timeOff) => new Date(timeOff.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 3);
  };

  // Get today's working hours
  const getTodayWorkingHours = () => {
    if (!availability?.workingHours) return null;
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const today = days[new Date().getDay()];
    return availability.workingHours[today];
  };

  const todayHours = getTodayWorkingHours();
  const upcomingTimeOff = getUpcomingTimeOff();

  // Quick action cards
  const quickActions = [
    {
      id: "status",
      title: "Update Status",
      description: "Change your availability status",
      icon: StatusOnlineIcon,
      color: "blue",
      action: () => onNavigate("status"),
    },
    {
      id: "working-hours",
      title: "Working Hours",
      description: "Manage your schedule",
      icon: ClockIcon,
      color: "purple",
      action: () => onNavigate("working-hours"),
    },
    {
      id: "time-off",
      title: "Time Off",
      description: "Request time off",
      icon: CalendarIcon,
      color: "orange",
      action: () => onNavigate("time-off"),
    },
    {
      id: "appointments",
      title: "Appointments",
      description: "Configure settings",
      icon: CogIcon,
      color: "green",
      action: () => onNavigate("appointments"),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Welcome back, {admin?.name?.split(" ")[0] || "Admin"}! 👋
            </h2>
            <p className="text-gray-600 mt-1">
              Here's an overview of your availability and upcoming schedule
            </p>
          </div>
          <div className="text-4xl">{getStatusIcon(status?.status)}</div>
        </div>
      </div>

      {/* Current Status Card */}
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <StatusOnlineIcon className="w-6 h-6 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">
              Current Status
            </h3>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("status")}
          >
            Update <ChevronRightIcon className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-3xl">{getStatusIcon(status?.status)}</div>
            <div>
              <Badge
                variant={getStatusColor(status?.status || "OFF_DUTY")}
                className="text-sm px-3 py-1"
              >
                {status?.status || "OFF_DUTY"}
              </Badge>
              {status?.customMessage && (
                <p className="text-sm text-gray-600 mt-2">
                  💬 {status.customMessage}
                </p>
              )}
              {status?.lastUpdated && (
                <p className="text-xs text-gray-500 mt-1">
                  Last updated: {new Date(status.lastUpdated).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {availability?.acceptsAppointments ? (
            <div className="text-center">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mx-auto mb-2"></div>
              <p className="text-sm text-green-700 font-medium">
                Accepting Appointments
              </p>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-3 h-3 bg-gray-400 rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 font-medium">Not Accepting</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <CalendarIcon className="w-8 h-8 text-blue-600" />
            <Badge variant="info" className="text-xs">
              Today
            </Badge>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.upcomingAppointments || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">Upcoming</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
          <div className="flex items-center justify-between mb-3">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
            <Badge variant="success" className="text-xs">
              Total
            </Badge>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.totalCompleted || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">Completed</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-5 border border-yellow-200">
          <div className="flex items-center justify-between mb-3">
            <StarIcon className="w-8 h-8 text-yellow-600" />
            <Badge variant="warning" className="text-xs">
              Avg
            </Badge>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.rating > 0 ? stats.rating.toFixed(1) : "N/A"}
          </p>
          <p className="text-sm text-gray-600 mt-1">Rating</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <TrendingUpIcon className="w-8 h-8 text-purple-600" />
            <Badge variant="default" className="text-xs">
              Rate
            </Badge>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.completedToday || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">Today</p>
        </div>
      </div>

      {/* Today's Schedule */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ClockIcon className="w-6 h-6 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">
              Today's Working Hours
            </h3>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onNavigate("working-hours")}
          >
            Edit <ChevronRightIcon className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {todayHours && !todayHours.isClosed ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-2xl">⏰</div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {formatTime(todayHours.start)} -{" "}
                    {formatTime(todayHours.end)}
                  </p>
                  <p className="text-sm text-gray-600">Regular working hours</p>
                </div>
              </div>
              <Badge variant="success">Open Today</Badge>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-4xl mb-2">🏠</div>
            <p className="text-gray-600 font-medium">
              You're not scheduled to work today
            </p>
          </div>
        )}
      </div>

      {/* Upcoming Time Off */}
      {upcomingTimeOff.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-gray-900">
                Upcoming Time Off
              </h3>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onNavigate("time-off")}
            >
              View All <ChevronRightIcon className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="space-y-3">
            {upcomingTimeOff.map((timeOff, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">📅</div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatDate(timeOff.date)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {timeOff.isFullDay
                        ? "Full Day"
                        : `${timeOff.startTime} - ${timeOff.endTime}`}
                    </p>
                  </div>
                </div>
                <Badge variant="info">{timeOff.type}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <UserGroupIcon className="w-5 h-5 text-primary" />
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.action}
                className={`bg-white border-2 border-${action.color}-200 rounded-lg p-5 hover:shadow-lg transition-all hover:border-${action.color}-400 text-left`}
              >
                <Icon className={`w-8 h-8 text-${action.color}-600 mb-3`} />
                <h4 className="font-semibold text-gray-900 mb-1">
                  {action.title}
                </h4>
                <p className="text-sm text-gray-600">{action.description}</p>
                <ChevronRightIcon className="w-5 h-5 text-gray-400 mt-2" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Tips & Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Availability Tips</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                Keep your status updated to help customers know when you're
                available
              </li>
              <li>
                Set up your working hours to automatically manage your
                availability
              </li>
              <li>Request time off in advance to prevent booking conflicts</li>
              <li>
                Configure appointment settings to control how customers book
                with you
              </li>
              <li>
                Enable notifications to stay informed about new appointments
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityDashboard;
